import React, { useEffect } from 'react';
import { AlertCircle, AlertTriangle, CheckCircle, Info, X } from 'lucide-react';
import { cn } from '../../utils/cn';

export interface AlertProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'default' | 'success' | 'warning' | 'error' | 'info';
  title?: string;
  description?: string;
  onClose?: () => void;
}

const variantClasses = {
  default: 'bg-[var(--app-surface-alt)] border-[var(--app-border)] text-[var(--app-text)]',
  success: 'bg-[#EAF7EE] border-emerald-200 text-[#1E7A3D]',
  warning: 'bg-amber-50 border-amber-200 text-amber-900',
  error: 'bg-red-50 border-red-200 text-red-900',
  info: 'bg-[var(--app-primary-tint)] border-[var(--app-primary)]/30 text-[var(--app-primary)]',
};

const icons = {
  default: Info,
  success: CheckCircle,
  warning: AlertTriangle,
  error: AlertCircle,
  info: Info,
};

export const Alert: React.FC<AlertProps> = ({
  className,
  variant = 'default',
  title,
  description,
  onClose,
  children,
  ...props
}) => {
  useEffect(() => {
    if (variant === 'success') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [variant]);

  const Icon = icons[variant];

  return (
    <div
      role="alert"
      className={cn(
        'relative flex gap-3 rounded-lg border p-4',
        variantClasses[variant],
        className
      )}
      {...props}
    >
      <Icon className="h-5 w-5 shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        {title ? <p className="font-semibold text-sm mb-0.5">{title}</p> : null}
        {description ? <p className="text-sm opacity-90">{description}</p> : null}
        {children}
      </div>
      {onClose ? (
        <button
          type="button"
          onClick={onClose}
          className="shrink-0 opacity-60 hover:opacity-100"
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      ) : null}
    </div>
  );
};
